import React, { useState, useEffect } from 'react';
import {
    createStudent,
    fetchStudentTypes,
    fetchStudentStatuses,
    fetchSexes,
    fetchCountries,
    fetchStates
} from '../utils/api';
import { mapErrorMessage } from '../utils/errorMapping';

const CreateStudentModal = ({ onClose, onStudentCreated }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [sex, setSex] = useState('');
    const [studentType, setStudentType] = useState('');
    const [mainStatus, setMainStatus] = useState('');
    const [innerStatus, setInnerStatus] = useState('');
    const [studentTypes, setStudentTypes] = useState({});
    const [studentStatuses, setStudentStatuses] = useState({});
    const [sexes, setSexes] = useState({});
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [error, setError] = useState('');
    const [note, setNote] = useState('');

    const [showAddressUS, setShowAddressUS] = useState(false);
    const [addressUS, setAddressUS] = useState({ line: '', city: '', state: '', zip_code: '' });

    const [showAddressHC, setShowAddressHC] = useState(false);
    const [addressHC, setAddressHC] = useState({ line: '', city: '', province: '', country: '', zip_code: '' });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [typesData, statusesData, sexesData, countriesData, statesData] = await Promise.all([
                    fetchStudentTypes(),
                    fetchStudentStatuses(),
                    fetchSexes(),
                    fetchCountries(),
                    fetchStates()
                ]);
                setStudentTypes(typesData.data);
                setStudentStatuses(statusesData.data);
                setSexes(sexesData.data);
                setCountries(countriesData.data);
                setStates(statesData.data);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };

        fetchInitialData();
    }, []);

    const handleAddressUSChange = (e) => {
        setAddressUS({ ...addressUS, [e.target.name]: e.target.value });
    };

    const handleAddressHCChange = (e) => {
        setAddressHC({ ...addressHC, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!firstName || !lastName || !email || !studentType || !mainStatus || !innerStatus) {
            setError('Please fill in all required fields.');
            return;
        }

        const body = {
            first_name: firstName,
            last_name: lastName,
            email: email,
            type: parseInt(studentType, 10),
            main_status: parseInt(mainStatus, 10),
            inner_status: parseInt(innerStatus, 10),
        };

        if (middleName) body.middle_name = middleName;
        if (phone) body.phone = phone;
        if (sex) body.sex = parseInt(sex, 10);

        if (showAddressUS) {
            body.address_us = {
                line: addressUS.line,
                city: addressUS.city,
                state: parseInt(addressUS.state, 10),
                zip_code: addressUS.zip_code,
            };
        }

        if (showAddressHC) {
            body.address_hc = {
                line: addressHC.line,
                city: addressHC.city,
                province: addressHC.province,
                country: parseInt(addressHC.country, 10),
                zip_code: addressHC.zip_code,
            };
        }

        if (note) body.note = note;

        try {
            await createStudent(body);
            onStudentCreated();
            onClose();
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleMainStatusChange = (e) => {
        setMainStatus(e.target.value);
        setInnerStatus('');
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>X</button>
                <h2>Create New Student</h2>
                {error && <p className="error">{error}</p>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First Name" />
                    </div>
                    <div className="form-group">
                        <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last Name" />
                    </div>
                    <div className="form-group">
                        <input type="text" value={middleName} onChange={(e) => setMiddleName(e.target.value)} placeholder="Middle Name" />
                    </div>
                    <div className="form-group">
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
                    </div>
                    <div className="form-group">
                        <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" />
                    </div>
                    <div className="form-group">
                        <select value={sex} onChange={(e) => setSex(e.target.value)}>
                            <option value="">Select Sex</option>
                            {Object.entries(sexes).map(([id, name]) => (
                                <option key={id} value={id}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <select value={studentType} onChange={(e) => setStudentType(e.target.value)}>
                            <option value="">Select Type</option>
                            {Object.entries(studentTypes).map(([id, name]) => (
                                <option key={id} value={id}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <select value={mainStatus} onChange={handleMainStatusChange}>
                            <option value="">Select Main Status</option>
                            {Object.entries(studentStatuses).map(([id, statuses]) => (
                                <option key={id} value={id}>{statuses[0]}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <select value={innerStatus} onChange={(e) => setInnerStatus(e.target.value)} disabled={!mainStatus}>
                            <option value="">Select Inner Status</option>
                            {mainStatus && studentStatuses[mainStatus] &&
                                Object.entries(studentStatuses[mainStatus]).map(([id, name]) => {
                                    if (id !== "0") {
                                        return <option key={id} value={id}>{name}</option>
                                    }
                                    return null;
                                })
                            }
                        </select>
                    </div>

                    <div className="collapsible-header" onClick={() => setShowAddressUS(!showAddressUS)}>
                        <button type="button" className="collapsible-button">
                            {showAddressUS ? 'Hide' : 'Show'} US Address
                        </button>
                    </div>
                    {showAddressUS && (
                        <div className="collapsible-content">
                            <div className="form-group">
                                <input type="text" name="line" value={addressUS.line} onChange={handleAddressUSChange} placeholder="Line" />
                            </div>
                            <div className="form-group">
                                <input type="text" name="city" value={addressUS.city} onChange={handleAddressUSChange} placeholder="City" />
                            </div>
                            <div className="form-group">
                                <select name="state" value={addressUS.state} onChange={handleAddressUSChange}>
                                    <option value="">Select State</option>
                                    {states.map((s) => (
                                        <option key={s.id} value={s.id}>{s.full_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <input type="text" name="zip_code" value={addressUS.zip_code} onChange={handleAddressUSChange} placeholder="Zip Code" />
                            </div>
                        </div>
                    )}

                    <div className="collapsible-header" onClick={() => setShowAddressHC(!showAddressHC)}>
                        <button type="button" className="collapsible-button">
                            {showAddressHC ? 'Hide' : 'Show'} Home Country Address
                        </button>
                    </div>
                    {showAddressHC && (
                        <div className="collapsible-content">
                            <div className="form-group">
                                <input type="text" name="line" value={addressHC.line} onChange={handleAddressHCChange} placeholder="Line" />
                            </div>
                            <div className="form-group">
                                <input type="text" name="city" value={addressHC.city} onChange={handleAddressHCChange} placeholder="City" />
                            </div>
                            <div className="form-group">
                                <input type="text" name="province" value={addressHC.province} onChange={handleAddressHCChange} placeholder="Province" />
                            </div>
                            <div className="form-group">
                                <select name="country" value={addressHC.country} onChange={handleAddressHCChange}>
                                    <option value="">Select Country</option>
                                    {countries.map((c) => (
                                        <option key={c.id} value={c.id}>{c.full_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <input type="text" name="zip_code" value={addressHC.zip_code} onChange={handleAddressHCChange} placeholder="Zip Code" />
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" />
                    </div>

                    <div className="actions-centered">
                        <button type="button" onClick={onClose} className="cancel-button text-button">Cancel</button>
                        <button type="submit" className="submit-button text-button">Create</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateStudentModal;